import { springboardAPI } from "src/boot/axios"
import { getCommonDB } from "src/db/databases"
import { SpringboardURL } from "src/util/enums/urls"
import { getCustomConfig, getNewAbortHandler } from "src/util/http/httpHelper"
import { ObjectList } from "src/util/types/dataModels/genericObject"
import { HttpConfig } from "src/util/types/dataModels/http"
import {
  PERMISSION_ACCESS_CREATE,
  PERMISSION_ACCESS_DELETE,
  PERMISSION_ACCESS_READ,
  PERMISSION_USER_ACCOUNTS,
  UserBasic,
  UserCurrent,
  UserPassword,
  UserRole,
} from "src/util/types/dataModels/user"

/**
 * The following endpoints are springboard and not CSI
 */

/**
 * Returns the current user data and settings
 */
export async function getCurrentUser(config: HttpConfig = {}): Promise<UserCurrent> {
  const url = SpringboardURL.USER_ME
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(abortHandler)
  const signal = abortHandler.controller.signal

  const { data } = await springboardAPI.get(url, { signal, customConfig })

  return data
}

/**
 * Updates the current user data and settings
 *
 * @param config
 * @param userData object of user data
 */
export async function patchCurrentUser(
  config: HttpConfig = {},
  userData: Partial<UserCurrent>
): Promise<UserCurrent> {
  const url = SpringboardURL.USER_ME
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(abortHandler)
  const signal = abortHandler.controller.signal

  const { data } = await springboardAPI.patch(url, userData, { signal, customConfig })

  return data
}

/**
 * Change the password for the current user
 *
 * @param config
 * @param passwords object of current and new passwords
 */
export async function patchChangePassword(config: HttpConfig = {}, passwords: UserPassword) {
  const url = SpringboardURL.USER_ME_PASSWORD
  const abortHandler = config?.abortHandler || getNewAbortHandler("PATCH", url)
  const customConfig = getCustomConfig(abortHandler)
  const signal = abortHandler.controller.signal

  await springboardAPI.patch(url, passwords, { signal, customConfig })
}

export async function getUserRoles(config: HttpConfig = {}): Promise<ObjectList<UserRole>> {
  const url = SpringboardURL.USER_ROLES
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(abortHandler)
  const signal = abortHandler.controller.signal

  const { data } = await springboardAPI.get(url, { signal, customConfig })

  return data
}

export async function getUser(config: HttpConfig = {}, userId: string): Promise<UserBasic> {
  const url = SpringboardURL.USER_ID.replace("{id}", userId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(abortHandler)
  const signal = abortHandler.controller.signal

  const { data } = await springboardAPI.get(url, { signal, customConfig })

  const db = await getCommonDB()
  await db.user.upsert(data)

  return data
}

export async function getUserImage(config: HttpConfig = {}, userId: string): Promise<Blob> {
  const url = SpringboardURL.USER_ID_IMAGE.replace("{id}", userId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const headers = { "Content-Type": "multipart/form-data" }

  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_USER_ACCOUNTS,
    PERMISSION_ACCESS_READ
  )
  const signal = abortHandler.controller.signal

  const { data } = await springboardAPI.get(url, {
    headers,
    signal,
    responseType: "blob",
    customConfig,
  })

  return data
}

export async function getCurrentUserImage(config: HttpConfig = {}): Promise<Blob> {
  const url = SpringboardURL.USER_ME_IMAGE
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const headers = { "Content-Type": "multipart/form-data" }

  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_USER_ACCOUNTS,
    PERMISSION_ACCESS_READ
  )
  const signal = abortHandler.controller.signal

  const { data } = await springboardAPI.get(url, {
    headers,
    signal,
    responseType: "blob",
    customConfig,
  })

  return data
}

export async function postCurrentUserImage(
  config: HttpConfig = {},
  image: File
): Promise<{ image: string }> {
  const url = SpringboardURL.USER_ME_IMAGE
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const headers = { "Content-Type": "multipart/form-data" }

  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_USER_ACCOUNTS,
    PERMISSION_ACCESS_CREATE
  )
  const signal = abortHandler.controller.signal

  // Expected format is { "image": "...data" }
  const formData = new FormData()
  formData.append("image", image)

  const { data } = await springboardAPI.post(url, formData, { headers, signal, customConfig })

  return data
}

export async function deleteCurrentUserImage(config: HttpConfig = {}) {
  const url = SpringboardURL.USER_ME_IMAGE
  const abortHandler = config?.abortHandler || getNewAbortHandler("DELETE", url)

  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_USER_ACCOUNTS,
    PERMISSION_ACCESS_DELETE
  )
  const signal = abortHandler.controller.signal

  await springboardAPI.delete(url, { signal, customConfig })
}
